import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Stack,
  Button,
  Drawer,
  IconButton,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  PAP_Design_Items_Getrow,
  PAP_Head_PostApi,
  PAP_Head_PDF_SaveApi,
} from "../../../Server/ApiV2";
import {
  exportTable,
  fAlertToast,
  hasNonEnterStatus,
} from "../../../Utility/Utilitys";
import DownloadIcon from "@mui/icons-material/Download";
import SideNavToolbar from "../Components/SidenavToolbar";
import SideNavHeader from "../Components/SideNavHeader";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export default function DesignDrawer({
  open,
  selectedPromo,
  setSelectedPromo,
  onClose,
  UserId,
  pageType,
  loadSummary,
}) {
  const [pages, setPages] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]);
  const [rowsData, setRowsData] = useState({});
  const [pdfName, setPdfName] = useState(selectedPromo?.PDF_URL || "");
  const [isEdited, setIsEdited] = useState(false);
  const [isPosted, setIsPosted] = useState(false);
  const [error, setError] = useState("");
  const vMediaQuery = useMediaQuery("(max-width:900px)");
  const xMediaQuery = useMediaQuery("(max-width:1400px)");
  const handleInputChange = (e) => {
    const value = e.target.value.trim();
    setPdfName(value);
    setIsEdited(true);
    setIsPosted(false);
    const pdfUrlPattern =
      /^(https?:\/\/)([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w\.-]*)*\/?(\?[^\s]*)?$/i;

    if (value && !pdfUrlPattern.test(value)) {
      setError("Please enter a valid public PDF URL");
    } else {
      setError("");
    }
  };

  const togglePage = (pageId) => {
    setSelectedPages((prev) =>
      prev.includes(pageId)
        ? prev.filter((p) => p !== pageId)
        : [...prev, pageId]
    );
  };

  const fPostHead = async () => {
    const result = await PAP_Head_PostApi({
      TransId: selectedPromo?.TransId,
      UserId: UserId,
      Typ: pageType === "Print" ? "P" : "PR",
    });

    if (result.status === 200) {
      fAlertToast("SUCCESS", "Posted Successfully!");
      await loadSummary();
      setIsPosted(true);
      onClose(); 
      setPdfName("");
    } else {
      fAlertToast("ERROR", result?.data?.message || "Post failed!");
    }
  };

  const filterRowByHeaders = (row, tableHeaders) => {
    return tableHeaders.reduce((acc, h) => {
      acc[h.label] = row?.[h.value] ?? "";
      return acc;
    }, {});
  };

  // Combined Single API Call
  const fLoadDesignItems = async () => {
    const result = await PAP_Design_Items_Getrow({
      TransId: selectedPromo?.TransId || 0,
    });

    if (result?.status === 200) {
      if (result?.data?.recordset?.pageData?.length > 0) {
        const pageData = result.data.recordset.pageData;

        // ✅ Prepare pages list for checkboxes
        const pageList = pageData.map((p, index) => ({
          PageId: p.subData?.[0]?.PageId || index + 1,
          SrNo: p.subData?.[0]?.SrNo || 0,
          label: p.Title || `Page ${index + 1}`,
        }));
        setPages(pageList);

        // ✅ Prepare rowsData (pageId → items)
        const rowsMap = {};
        pageData.forEach((p) => {
          const pageId = p.subData?.[0]?.PageId;
          rowsMap[pageId] = p.subData || [];
        });
        setRowsData(rowsMap);

        // ✅ Auto-select all pages by default
        const allPageIds = pageList.map((p) => p.PageId);
        setSelectedPages(allPageIds);
      }
    } else {
      setPages([]);
      setRowsData({});
    }
  };

  const fSave = async () => {
    const urlPattern =
      /^(https?:\/\/)([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w\.-]*)*\/?(\?[^\s]*)?$/i;

    if (!pdfName || pdfName.trim() === "" || !urlPattern.test(pdfName.trim())) {
      fAlertToast("ERROR", "Please enter a valid public URL before saving.");
      setError("Please enter a valid public URL before saving.");
      return;
    }

    const obj = {
      TransId: selectedPromo?.TransId,
      PDF_URL: pdfName.trim(),
    };

    const result = await PAP_Head_PDF_SaveApi(obj);

    if (result?.status === 200) {
      fAlertToast("SUCCESS", "Saved Successfully!");
      setError("");
      setSelectedPromo((prev) => ({
        ...prev,
        PDF_URL: pdfName.trim(),
      }));
      loadSummary();
    } else {
      fAlertToast("ERROR", result?.data?.message || "Save failed!");
    }
  };

  useEffect(() => {
    if (open) {
      setPages([]);
      setSelectedPages([]);
      setRowsData({});
      setPdfName(selectedPromo?.PDF_URL || "");
      setError("");
      fLoadDesignItems();
    }
  }, [open]);

  const tableHeaders = [
    { label: "Item Name", value: "ItemName", align: "left", width: 200 },
    { label: "Unit", value: "Unit", align: "left", width: 30 },
    { label: "Cut.Price", value: "CutPrice", align: "right", width: 80 },
    { label: "Disc.%", value: "DiscountPerc", align: "right", width: 70 },
    {
      label: "Promo.RSP-(VAT)",
      value: "PromoRSPWithVat",
      align: "right",
      width: 100,
    },
    { label: "Qty/Inv", value: "QtyPerPromotion", align: "center", width: 90 },
    { label: "Type", value: "PromoTyp", align: "left", width: 70 },
    { label: "Remarks", value: "Remarks", align: "left", width: 200 },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      sx={{
        "& .MuiDrawer-paper": {
          mt: 5,
          width: { xs: "100%", sm: "100%", md: "90%" },
          height: "100vh",
          borderTopLeftRadius: "12px",
          borderBottomLeftRadius: "12px",
          height: "96vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden", 
        },
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "linear-gradient(135deg, #1e88e5 0%, #673ab7 100%)",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1.2,
          borderBottom: "1px solid #1565c0",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            onClick={() => {
              setPdfName("");
              setSelectedPages([]);
              setRowsData({});
              setError("");
              onClose();
            }}
            sx={{
              color: "#ffffff",
              backgroundColor: "rgba(255,255,255,0.15)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.25)",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            {pageType === "Design" ? "Design" : "Proof Reading"}
          </Typography>
        </Box>
        <IconButton
          onClick={() => {
            setPdfName("");
            setSelectedPages([]);
            setRowsData({});
            setError("");
            onClose();
          }}
          sx={{
            color: "#ffffff",
            backgroundColor: "rgba(255,255,255,0.15)",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.25)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <SideNavToolbar
          transId={selectedPromo?.TransId || ""}
          selectedRow={selectedPromo}
          userCd={UserId}
          pageType={pageType}
          loadSummary={loadSummary}
        />
        <Box sx={{ paddingX: 2 }}>
          <SideNavHeader selectedRow={selectedPromo} pageType={pageType} />
        </Box>
        {pageType !== "Design" && (
          <Box
            sx={{
              flexGrow: 1,
              px: 3,
              pb: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                textAlign: "center",
                justifyContent: "center",
                marginTop: "1%",
              }}
            >
              {pdfName ? (
                <Box
                  sx={{
                    width: "100%",
                    height: { xs: "55vh", sm: "70vh" },
                    mb: 2,
                    borderRadius: "10px",
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  }}
                >
                  <iframe
                    src={`${pdfName}#toolbar=0&navpanes=0&scrollbar=0`}
                    style={{
                      width: vMediaQuery
                        ? "100%"
                        : xMediaQuery
                        ? "100%"
                        : "70%",
                      height: "100%",
                      border: "none",
                    }}
                    title="Proof Reading PDF"
                  />
                </Box>
              ) : (
                <Typography color="error" sx={{ p: 2 }}>
                  No PDF uploaded for this promotion.
                </Typography>
              )}
            </Box>
          </Box>
        )}
        {pageType === "Design" && (
          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              px: 3,
              pb: 12,
              "&::-webkit-scrollbar": { width: 8 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#b0bec5",
                borderRadius: 4,
              },
            }}
          >

            <Box sx={{ mt: 2 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1, width: "100%" }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography fontWeight={600}>Page Selection:</Typography>
                  {pages?.length > 0 && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            pages?.length > 0 &&
                            selectedPages?.length === pages?.length
                          }
                          indeterminate={
                            selectedPages?.length > 0 &&
                            selectedPages?.length < pages?.length
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPages(pages.map((p) => p.PageId));
                            } else {
                              setSelectedPages([]);
                            }
                          }}
                        />
                      }
                      label="Select All"
                      sx={{
                        "& .MuiTypography-root": {
                          fontSize: "14px",
                          fontWeight: 500,
                        },
                      }}
                    />
                  )}
                </Stack>
                <Tooltip title="Export CSV">
                  <span>
                    <IconButton
                      color="primary"
                      sx={{
                        p: { xs: 1, sm: 1.5 },
                      }}
                      onClick={() => {
                        const exportData = pages.flatMap((page) => [
                          { PageHeader: page.label },

                          ...(rowsData[page.PageId] || []).map((item) => ({
                            TransId: selectedPromo?.TransId,
                            PageId: page.PageId, // keep this if you need it
                            ...filterRowByHeaders(item, tableHeaders),
                          })),

                          {},
                        ]);

                        exportTable(exportData, pageType, "Design");
                      }}
                    >
                      <DownloadIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>

              {/* Checkbox Grid */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                  gap: 0,
                  mt: 1,
                  rowGap: 0,
                }}
              >
                {pages.length > 0 ? (
                  pages.map((p) => (
                    <FormControlLabel
                      key={p.PageId}
                      control={
                        <Checkbox
                          checked={selectedPages?.includes(p.PageId)}
                          onChange={() => togglePage(p.PageId, p.SrNo)}
                        />
                      }
                      label={p.label}
                    />
                  ))
                ) : (
                  <Typography color="gray">No pages available.</Typography>
                )}
              </Box>
            </Box>
            {selectedPages?.map((pageId) => {
              const pageObj = pages.find((p) => p.PageId === pageId);
              return (
                <Paper
                  key={pageId}
                  elevation={1}
                  sx={{
                    mt: 3,
                    mb: 2,
                    p: 2,
                    borderRadius: 4,
                    bgcolor: "#fafcff",
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: "primary.light",
                      color: "white",
                      p: 1,
                      borderRadius: 1,
                      mb: 1,
                    }}
                  >
                    <Typography fontWeight={600}>
                      {(pageObj?.label || `Page ${pageId}`).toUpperCase()}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      width: "100%",
                      overflowX: "auto",
                      "&::-webkit-scrollbar": { height: 6 },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#0d73bb",
                        borderRadius: 3,
                      },
                    }}
                  >
                    <TableContainer
                      sx={{ minWidth: "1300px", overflowX: "unset" }}
                    >
                      <Table
                        sx={{
                          width: "99%",
                          borderCollapse: "separate",
                          borderSpacing: "0 8px",
                          tableLayout: "fixed",
                        }}
                      >
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#f0f5ff" }}>
                            {tableHeaders.map((h) => (
                              <TableCell
                                key={h.value}
                                align={h.align}
                                sx={{
                                  width: h.width,
                                  minWidth: h.width,
                                  maxWidth: h.width,
                                  fontWeight: 600,
                                  padding: "8px",
                                  fontSize: "13px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  borderBottom: "1px solid #ddd",
                                }}
                              >
                                {h.label}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {rowsData[pageId]?.length > 0 ? (
                            <>
                              {rowsData[pageId]?.map((row, i) => (
                                <TableRow
                                  key={i}
                                  sx={{
                                    backgroundColor: "#fff",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                                    borderRadius: "10px",
                                    cursor: "pointer",
                                    transition: "all 0.25s ease",
                                    "&:hover": {
                                      backgroundColor: "#f0f5ff",
                                      transform: "scale(1.01)",
                                    },
                                  }}
                                >
                                  {tableHeaders.map((h) => (
                                    <TableCell
                                      key={h.value}
                                      align={h.align}
                                      sx={{
                                        width: h.width,
                                        minWidth: h.width,
                                        maxWidth: h.width,
                                        fontSize: "11px",
                                        padding: "10px",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      {row[h.value] ?? ""}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </>
                          ) : (
                            <div style={{ textAlign: "center" }}>
                              <Typography color="gray">
                                No items available.
                              </Typography>
                            </div>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
        {pageType === "Design" ? (
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              width: "100%",
              p: 2,
              bgcolor: "#f9fbff",
              borderTop: "2px solid #e0e0e0",
              boxShadow: "0 -2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <Stack
              direction={{ xs: "row", sm: "row" }}
              spacing={2}
              alignItems="center"
              sx={{ width: "100%" }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  color: "#0d73bb",
                  whiteSpace: "nowrap",
                  display: { xs: "none", sm: "block" },
                }}
              >
                CLOUD PDF:
              </Typography>

              <Box sx={{ flexGrow: 1 }}>
                <input
                  type="url"
                  placeholder="Enter PDF name or link"
                  value={pdfName}
                  onChange={handleInputChange}
                  disabled={selectedPromo?.ProofRead_Status === "POST"}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: error ? "1px solid red" : "1px solid #ccc",
                    outline: "none",
                    width: "100%",
                    fontSize: "14px",
                    backgroundColor:
                      selectedPromo?.ProofRead_Status === "POST"
                        ? "#f3f3f3"
                        : "white",
                  }}
                />
                {error && (
                  <Typography
                    variant="caption"
                    sx={{ color: "red", mt: 0.5, display: "block" }}
                  >
                    {error}
                  </Typography>
                )}
              </Box>

              <Button
                variant="contained"
                color="primary"
                disabled={hasNonEnterStatus(selectedPromo)}
                sx={{ textTransform: "none", px: 5, py: 1, fontWeight: 600 }}
                onClick={fSave}
              >
                Save
              </Button>
            </Stack>
          </Box>
        ) : (
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              width: "100%",
              pt: 1,
              pb: 2,
              bgcolor: "#f9fbff",
              borderTop: "2px solid #e0e0e0",
              boxShadow: "0 -2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="center"
              alignItems="center"
              spacing={2}
            >
              <Button
                variant="contained"
                color="success"
                disabled={hasNonEnterStatus(selectedPromo)}
                sx={{ textTransform: "none", px: 5, py: 1, fontWeight: 600 }}
                onClick={fPostHead}
              >
                Post
              </Button>
            </Stack>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
